import { Link, Navigate, useParams } from "react-router-dom";
import { Phone, ArrowRight, MapPin, Check } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import CTASection from "../components/CTASection";
import { Seo } from "../components/Seo";
import { BUSINESS, TOWN_PAGES, SIZE_PRICING, CLEANUP_UPSELLS, STANDARD_SERVICE, IMAGES } from "../lib/site";

const SITE_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const PRICE_ROWS = SIZE_PRICING.filter((p) => ['15"', '20"', '30"', '40"', '50"', '70"'].includes(p.size));

const townFaq = (town) => [
    {
        q: `How fast can you get to ${town.name}?`,
        a: `Same week — guaranteed. ${town.miles === 0 ? "You're in our home town, so it's often the next day." : `${town.name} is about ${town.miles} miles from our shop in Poplar Bluff, well inside the 30-mile radius we cover every week.`}`,
    },
    {
        q: "Is there a trip or travel charge?",
        a: `No. Anywhere inside our 30-mile radius — including ${town.name} — is priced straight off the size and difficulty chart. The only add-ons are the ones you can see on our pricing page.`,
    },
    {
        q: "What's left when you're done?",
        a: `${STANDARD_SERVICE} If you'd rather have the chips hauled off, or want topsoil and seed so the spot disappears, those are flat add-ons you can pick on the quote form.`,
    },
];

const ServiceTown = () => {
    const { slug } = useParams();
    const town = TOWN_PAGES.find((t) => t.slug === slug);
    if (!town) return <Navigate to="/" replace />;

    const others = TOWN_PAGES.filter((t) => t.slug !== slug);
    const title = `Stump Grinding in ${town.name}, MO`;
    const description = `Stump grinding and cleanup in ${town.name}, Missouri (${town.county}). Upfront prices by stump size from $150, no travel charge, same-week guarantee. Call or text (573) 300-8625.`;
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Stump grinding",
        name: title,
        description,
        url: `${SITE_URL}/stump-grinding/${town.slug}`,
        areaServed: { "@type": "City", name: `${town.name}, MO` },
        provider: { "@type": "LocalBusiness", "@id": `${SITE_URL}/#business`, name: BUSINESS.name, telephone: "+1-573-300-8625" },
        offers: { "@type": "Offer", priceCurrency: "USD", price: "150", description: "Starting price for stumps up to 15 inches" },
    };

    return (
        <main className="bg-brand-ink" data-testid={`town-page-${town.slug}`}>
            <Seo title={title} description={description} jsonLd={jsonLd} />

            <section className="relative overflow-hidden border-b border-brand-line">
                <div className="absolute inset-0">
                    <img src={IMAGES.stumpField} alt="" aria-hidden="true" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-brand-ink/90 via-brand-ink/85 to-brand-ink" />
                </div>
                <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-40 pb-20">
                    <Reveal className="max-w-3xl">
                        <Eyebrow className="mb-4">Stump grinding · {town.county}</Eyebrow>
                        <h1 className="font-display font-black uppercase text-brand-cream text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.92] mb-6" data-testid="town-h1">
                            Stump grinding in <span className="text-brand-amber">{town.name}, MO.</span>
                        </h1>
                        <p className="text-brand-sage text-base sm:text-lg leading-relaxed mb-6">{town.blurb}</p>
                        <p className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-cream border border-brand-line bg-brand-ink/70 rounded-full px-4 py-2 mb-10" data-testid="town-distance">
                            <MapPin className="w-3.5 h-3.5 text-brand-amber" />
                            {town.miles === 0 ? "Home base · Poplar Bluff" : `About ${town.miles} miles from Poplar Bluff · inside our 30-mile radius`}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <a href={BUSINESS.phoneTel} data-testid="town-call-cta" className="inline-flex items-center justify-center gap-3 bg-brand-amber text-brand-ink font-bold text-base sm:text-lg px-8 py-4 rounded-full hover:bg-brand-amberHover transition-colors">
                                <Phone className="w-5 h-5" />
                                Call {BUSINESS.phoneDisplay}
                            </a>
                            <Link to="/quote" data-testid="town-quote-cta" className="group inline-flex items-center justify-center gap-3 border border-brand-cream/30 text-brand-cream font-semibold text-base sm:text-lg px-8 py-4 rounded-full hover:border-brand-amber hover:text-brand-amber transition-colors">
                                Free Quote
                                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
                <Reveal>
                    <Eyebrow className="mb-4">What {town.name} pays</Eyebrow>
                    <h2 className="font-display font-black uppercase text-brand-cream text-4xl sm:text-5xl tracking-tight leading-[0.95] mb-6">
                        Same price sheet, <span className="text-brand-amber">no trip fee.</span>
                    </h2>
                    <ul className="divide-y divide-brand-line border-y border-brand-line mb-6" data-testid="town-price-list">
                        {PRICE_ROWS.map((p) => (
                            <li key={p.size} className="flex items-center justify-between py-3">
                                <span className="font-mono text-sm text-brand-sage">Stump up to {p.size} across</span>
                                <span className="font-display font-black text-brand-cream text-xl">${p.price}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="text-brand-sage text-sm leading-relaxed mb-5">
                        Measure the widest point across the top of the stump. Difficult access, big roots or rocky
                        ground are listed add-ons — you'll see them before we start.
                    </p>
                    <Link to="/pricing" data-testid="town-pricing-link" className="inline-flex items-center gap-2 text-brand-amber font-semibold hover:underline">
                        Full price list &amp; instant estimator <ArrowRight className="w-4 h-4" />
                    </Link>
                </Reveal>

                <Reveal delay={0.1}>
                    <Eyebrow className="mb-4">Included every time</Eyebrow>
                    <h2 className="font-display font-black uppercase text-brand-cream text-4xl sm:text-5xl tracking-tight leading-[0.95] mb-6">
                        Grind it. <span className="text-brand-amber">Clean it.</span>
                    </h2>
                    <p className="text-brand-sage text-base leading-relaxed mb-6">{STANDARD_SERVICE}</p>
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-moss mb-3">Optional cleanup add-ons</p>
                    <ul className="space-y-2.5" data-testid="town-cleanup-list">
                        {CLEANUP_UPSELLS.map((c) => (
                            <li key={c.label} className="flex items-center justify-between gap-4 text-sm">
                                <span className="flex items-center gap-2 text-brand-cream">
                                    <Check className="w-4 h-4 text-brand-amber shrink-0" />
                                    {c.label}
                                </span>
                                <span className="font-mono text-brand-sage">{c.add}</span>
                            </li>
                        ))}
                    </ul>
                </Reveal>
            </section>

            <section className="border-y border-brand-line bg-brand-surface">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {townFaq(town).map((f, i) => (
                        <Reveal key={f.q} delay={i * 0.06}>
                            <h3 className="font-display font-bold uppercase text-brand-cream text-xl tracking-tight mb-3">{f.q}</h3>
                            <p className="text-brand-sage text-sm leading-relaxed">{f.a}</p>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
                <Reveal>
                    <Eyebrow className="mb-6">Also serving</Eyebrow>
                    <div className="flex flex-wrap gap-2" data-testid="town-other-links">
                        {others.map((t) => (
                            <Link
                                key={t.slug}
                                to={`/stump-grinding/${t.slug}`}
                                data-testid={`town-link-${t.slug}`}
                                className="font-mono text-[11px] uppercase tracking-wider rounded-full px-3.5 py-1.5 border border-brand-line text-brand-sage hover:border-brand-amber hover:text-brand-amber transition-colors"
                            >
                                {t.name}
                            </Link>
                        ))}
                    </div>
                </Reveal>
            </section>

            <CTASection testid="town-cta" />
        </main>
    );
};

export default ServiceTown;
